import React, { useMemo } from "react";
import classNames from "classnames";
import stylesLess from "./index.module.less";
import { useRouter } from "next/router";
import { Platform } from "@xt/xtbase";
import Nav, { GlobalHeader, SiteFooter } from "@xt/Nav";
import LandingNav from "components/Pages/Landing/LandingNav";
import Foot from "@xt/Foot";
import useIsDesktop from "@/hooks/useIsDesktop";
import SpikexPcBackdrop from "./SpikexPcBackdrop";

export default function Layout({
  children,
  isShowNav = true,
  isShowFoot = false,
  /** PC：对齐 spikex-ai-web 的 SiteFooter（替代旧 Foot）；本地先不 publish Nav */
  useSiteFooter = false,
  style = {},
  useBottomBar = true,
  /** 仅保留底部 Home Tab，隐藏移动端顶部 NavbarMb（用于页面内自带顶栏的页面如 /convert/buy） */
  bottomBarHideTop = false,
  /** @deprecated PC 遮罩已移除，保留 prop 兼容旧页面 */
  disablePcMobileTip = false,
  landingNav = false,
  /** PC 账户页：左侧菜单固定，仅右侧内容区滚动 */
  accountDesktopSplit = false,
}) {
  const router = useRouter();
  const { isDesktop, isReady } = useIsDesktop();
  const isInApp = Platform.isInApp();

  const isHomePage = router.pathname === "/" || router.route === "/";
  const showNav = !Platform.isInApp() && isShowNav !== false;
  const showFoot = !Platform.isInApp() && isShowFoot !== false;

  /** 移动端：顶部 Navbar 仅首页展示；非首页保留 BottomBar（若启用） */
  const showTopNav = showNav && !landingNav && isHomePage && !bottomBarHideTop;
  const effectiveBottomBarHideTop =
    bottomBarHideTop || (useBottomBar && showNav && !landingNav && !isHomePage);

  /** 宽屏 PC：GlobalHeader + 无 BottomBar；窄屏：沿用 H5 BottomBar */
  const isDesktopNav = isReady && isDesktop && showNav;
  const hasBottomBar = useBottomBar && !landingNav && !isInApp && (!isReady || !isDesktop);

  const isFixedNav =
    !isInApp &&
    (isDesktopNav ||
      landingNav ||
      (showNav && !(useBottomBar && effectiveBottomBarHideTop && !isDesktop)));

  const styles = useMemo(() => {
    const baseStyle = { ...style };
    if (hasBottomBar) {
      baseStyle.paddingBottom = "calc(70px + env(safe-area-inset-bottom, 0px))";
    }
    return baseStyle;
  }, [style, hasBottomBar]);

  const renderNav = () => {
    if (isDesktopNav) {
      return <GlobalHeader mode="fixed" />;
    }
    if (landingNav) {
      return <LandingNav />;
    }
    if (useBottomBar && effectiveBottomBarHideTop) {
      return <Nav mode="fixed" bgColor="#181a20" bottomBar bottomBarHideTop />;
    }
    if (useBottomBar) {
      return <Nav mode="fixed" bgColor="#181a20" bottomBar />;
    }
    if (showTopNav) {
      return <Nav mode="fixed" bgColor="#181a20" />;
    }
    return null;
  };

  const shouldRenderNav =
    !isInApp && (isDesktopNav || landingNav || (showNav && (useBottomBar || showTopNav)));

  return (
    <div
      className={classNames(
        stylesLess.page,
        stylesLess.pageSpikexFont,
        accountDesktopSplit && isDesktopNav && stylesLess.pageAccountDesktopSplit,
      )}
      style={styles}
    >
      {isDesktopNav ? <SpikexPcBackdrop /> : null}
      <div className={stylesLess.pageInner}>
        {shouldRenderNav ? renderNav() : null}
        <div
          className={`${stylesLess.main} ${
            isFixedNav
              ? isDesktopNav
                ? stylesLess.mainFixedNavDesktop
                : landingNav
                  ? stylesLess.mainFixedNavLanding
                  : stylesLess.mainFixedNav
              : ""
          }`}
        >
          {children}
        </div>
        {showFoot ? (useSiteFooter ? <SiteFooter /> : <Foot />) : null}
      </div>
    </div>
  );
}
